import React, { useId } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBinoculars } from '@fortawesome/free-solid-svg-icons'
import {
  actionTagClass,
  customerTagClass,
  describeRule,
  pageFromUrl,
  pagerMeta,
  sourceLabel,
} from './blocksExplorerHelpers'
import TableHeader from '../../../components/TableHeader'
import { IconWithHelperText } from '../../../components/TableRow'
import FindingsBadge from './FindingsBadge'
import Pagination from '../../../components/Pagination'
import NoRecord from '../../../components/elements/NoRecord'

// TableHeader hides a column named `id` from users without show_ids; a block's number must stay visible.
const COLUMNS = [
  { column_name: 'block_id', label_name: '#', list_size: 2, sortKey: 'id' },
  { column_name: 'name', label_name: 'Block', list_size: 8, sortKey: 'name' },
  { column_name: 'rule', label_name: 'Rule', list_size: 10, sortKey: 'rule' },
  {
    column_name: 'action',
    label_name: 'Action',
    list_size: 4,
    sortKey: 'action',
  },
  {
    column_name: 'customer',
    label_name: 'Customer',
    list_size: 3,
    sortKey: 'customer',
  },
  {
    column_name: 'source',
    label_name: 'Source',
    list_size: 5,
    sortKey: 'source',
  },
]

export const PER_PAGE_OPTIONS = [20, 50, 100]

const ruleText = (explorerRow) =>
  describeRule({
    table: explorerRow.table,
    row: explorerRow.block.row,
    conditions: explorerRow.block.conditions,
  })

export function BlocksPager({
  total,
  page,
  totalPages,
  perPage,
  onPageChange,
  onPerPageChange,
}) {
  return (
    <div data-testid="blocks-pager">
      {totalPages > 1 && (
        <Pagination
          meta={pagerMeta({ page, totalPages })}
          setPartialLoad={() => {}}
          paginateCallback={(url) => onPageChange(pageFromUrl(url))}
        />
      )}
      <div className="lox-blocks-explorer__pager">
        <span
          className="is-size-7 has-text-grey"
          data-testid="blocks-result-count"
        >
          {total} result{total === 1 ? '' : 's'}
        </span>
        <label className="is-size-7 has-text-grey lox-blocks-explorer__per-page">
          Rows per page
          <span className="select is-small ml-2">
            <select
              value={perPage}
              onChange={(event) => onPerPageChange(Number(event.target.value))}
              data-testid="blocks-per-page"
            >
              {PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </span>
        </label>
      </div>
    </div>
  )
}

export default function BlocksTable({
  rows,
  sort,
  onSort,
  actionLabels,
  treatmentNamesById,
  treatmentId,
  selectedBlock,
  onSelect,
}) {
  const startId = useId()

  if (rows.length === 0) {
    return <NoRecord />
  }

  const selectRow = (explorerRow) =>
    onSelect({ table: explorerRow.table, id: explorerRow.block.row.id })

  const sortedColumn = COLUMNS.find((column) => column.sortKey === sort.key)
  const sortByColumn = (columnName) =>
    onSort(COLUMNS.find((column) => column.column_name === columnName).sortKey)

  // Arrow keys walk the rows so the drawer can be driven from the keyboard.
  const handleKeyDown = (event, explorerRow) => {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      selectRow(explorerRow)
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const sibling =
        event.key === 'ArrowDown'
          ? event.currentTarget.nextElementSibling
          : event.currentTarget.previousElementSibling
      sibling?.focus()
    }
  }

  return (
    <div className="lox-hooktable table-container mb-0 position-relative">
      <div className="list-table-container">
        <table
          className="lox-hooktable-table table is-striped is-narrow is-hoverable is-fullwidth is-info has-sticky-column has-view-button"
          data-testid="blocks-table"
        >
          <TableHeader
            columns={COLUMNS}
            startId={startId}
            hasView
            sortableFields={COLUMNS}
            onSort={sortByColumn}
            sortState={{
              column_name: sortedColumn?.column_name ?? null,
              direction: sort.direction,
            }}
          />
          <tbody>
            {rows.map((explorerRow) => {
              const {
                row,
                is_override: isOverride,
                source_treatment_id: sourceTreatmentId,
              } = explorerRow.block
              const rowKey = `${explorerRow.table}-${row.id}`
              const isSelected =
                selectedBlock?.table === explorerRow.table &&
                selectedBlock?.id === row.id
              return (
                <tr
                  key={rowKey}
                  className={`lox-blocks-explorer__row ${
                    isSelected ? 'is-selected' : ''
                  }`}
                  tabIndex={0}
                  aria-selected={isSelected}
                  onClick={() => selectRow(explorerRow)}
                  onKeyDown={(event) => handleKeyDown(event, explorerRow)}
                  data-testid={`blocks-table-row-${rowKey}`}
                >
                  <th className="fixed sticky">
                    <button
                      type="button"
                      className="button is-primary lox-view-button"
                      aria-label={
                        row.id < 0 ? 'View new block' : `View block #${row.id}`
                      }
                      onClick={(event) => {
                        event.stopPropagation()
                        selectRow(explorerRow)
                      }}
                    >
                      <IconWithHelperText
                        icon={faBinoculars}
                        helperText="View Record"
                      />
                    </button>
                  </th>
                  <td className="lox-blocks-explorer__id">
                    {row.id < 0 ? (
                      <span className="tag is-success is-light lox-no-border">
                        New
                      </span>
                    ) : (
                      `#${row.id}`
                    )}
                  </td>
                  <td>
                    {row.name ?? <span className="has-text-grey">—</span>}
                    {explorerRow.findings.length > 0 && (
                      <span className="ml-2">
                        <FindingsBadge
                          findings={explorerRow.findings}
                          showCount
                        />
                      </span>
                    )}
                  </td>
                  <td>
                    <code className="lox-blocks-explorer__rule-pill">
                      {ruleText(explorerRow)}
                    </code>
                  </td>
                  <td>
                    <span
                      className={`tag lox-no-border ${actionTagClass(
                        row.block_action
                      )}`}
                    >
                      {actionLabels[row.block_action] ??
                        `Action ${row.block_action}`}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`tag lox-no-border ${customerTagClass(
                        row.customer_type
                      )}`}
                    >
                      {row.customer_type ?? '—'}
                    </span>
                  </td>
                  <td>
                    <span className="tag is-light lox-no-border">
                      {sourceLabel(sourceTreatmentId, treatmentNamesById)}
                    </span>
                    {isOverride && sourceTreatmentId === treatmentId && (
                      <span className="tag is-info is-light lox-no-border lox-blocks-explorer__overridden">
                        Overridden here
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
